<template>
  <a-layout class="container">
    <a-layout-content class="content">
      <!-- 第二部分：堆叠柱形图 -->
      <div class="chart-section">
        <a-card title="各学段专任教师人数趋势（堆叠柱形图）" class="chart-card">
          <div class="chart-container">
            <v-chart :option="teacherChartOption" :autoresize="true" style="height: 500px" />
          </div>
        </a-card>
      </div>

      <!-- 第一部分：两个统计表 -->
      <div class="tables-section">
        <a-row :gutter="16">
          <!-- 左侧：教师人数表 -->
          <a-col :span="12">
            <a-card title="专任教师人数统计" class="table-card">
              <a-table
                ref="teacherTableRef"
                :columns="teacherColumns"
                :data="teacherData"
                :pagination="false"
                :scroll="{ x: 1200, y: 400 }"
                bordered
              >
                <template #cell="{ column, record }">
                  <span v-if="column.dataIndex === 'segment'">
                    {{ record.segment }}
                  </span>
                  <span v-else>
                    {{ formatToInt(record[column.dataIndex]) }}
                  </span>
                </template>
              </a-table>
            </a-card>
          </a-col>

          <!-- 右侧：教师余缺数表 -->
          <a-col :span="12">
            <a-card title="教师余缺情况预测" class="table-card">
              <a-table
                ref="surplusTableRef"
                :columns="teacherSurplusColumns"
                :data="teacherSurplusData"
                :pagination="false"
                :scroll="{ x: 1200, y: 400 }"
                bordered
              >
                <template #cell="{ column, record }">
                  <span v-if="column.dataIndex === 'segment'">
                    {{ record.segment }}
                  </span>
                  <span v-else :class="getValueClass(record[column.dataIndex])">
                    {{ formatToInt(record[column.dataIndex]) }}
                  </span>
                </template>
              </a-table>
              <div class="table-note">
                <p>
                  说明：预测数据为负说明教师存在缺口，预测数据为正说明教师出现富余（以2024年数据为基数进行比较测算）。
                </p>
              </div>
            </a-card>
          </a-col>
        </a-row>
      </div>
    </a-layout-content>
  </a-layout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Card, Layout, Table, Row, Col } from '@arco-design/web-vue';
import VChart from 'vue-echarts';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  MarkLineComponent,
} from 'echarts/components';

/* ---------- 注册 ECharts ---------- */
use([
  CanvasRenderer,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  MarkLineComponent,
]);

/* ---------- 表格 ref ---------- */
const teacherTableRef = ref();
const surplusTableRef = ref();

/* ---------- 数据 ---------- */
const teacherData = ref([
  {
    key: '1',
    segment: '幼儿园',
    2015: 2442,
    2016: 2587,
    2017: 2854,
    2018: 3165,
    2019: 3592,
    2020: 4070,
    2021: 4232,
    2022: 4517,
    2023: 4362,
    2024: 4024,
    2025: 3636,
    2026: 3321,
    2027: 3330,
    2028: 3277,
    2029: 3185,
    2030: 3026,
    2031: 2956,
    2032: 2887,
    2033: 2820,
    2034: 2786,
    2035: 2785,
  },
  {
    key: '2',
    segment: '小学',
    2015: 10781,
    2016: 10687,
    2017: 10563,
    2018: 10466,
    2019: 10436,
    2020: 10450,
    2021: 10363,
    2022: 10085,
    2023: 10040,
    2024: 9902,
    2025: 9821,
    2026: 9967,
    2027: 9731,
    2028: 9332,
    2029: 8444,
    2030: 7834,
    2031: 7332,
    2032: 6978,
    2033: 6802,
    2034: 6723,
    2035: 6650,
  },
  {
    key: '3',
    segment: '初中',
    2015: 6565,
    2016: 6598,
    2017: 6644,
    2018: 6552,
    2019: 6467,
    2020: 6444,
    2021: 6374,
    2022: 6164,
    2023: 6008,
    2024: 5869,
    2025: 5898,
    2026: 6127,
    2027: 6051,
    2028: 6058,
    2029: 6575,
    2030: 6920,
    2031: 7033,
    2032: 6323,
    2033: 5669,
    2034: 5034,
    2035: 4584,
  },
  {
    key: '4',
    segment: '普通高中',
    2015: 2650,
    2016: 2724,
    2017: 2864,
    2018: 2946,
    2019: 3083,
    2020: 3110,
    2021: 3170,
    2022: 3162,
    2023: 3149,
    2024: 3129,
    2025: 3109,
    2026: 3317,
    2027: 3427,
    2028: 3558,
    2029: 3566,
    2030: 3623,
    2031: 3993,
    2032: 4257,
    2033: 4381,
    2034: 3982,
    2035: 3594,
  },
]);

const teacherSurplusData = ref([
  {
    key: '1',
    segment: '学前教师缺口/富余',
    2025: 388,
    2026: 315,
    2027: -9,
    2028: 53,
    2029: 92,
    2030: 159,
    2031: 70,
    2032: 69,
    2033: 68,
    2034: 34,
    2035: 0,
  },
  {
    key: '2',
    segment: '小学教师缺口/富余',
    2025: 81,
    2026: -146,
    2027: 236,
    2028: 399,
    2029: 888,
    2030: 610,
    2031: 501,
    2032: 354,
    2033: 176,
    2034: 79,
    2035: 73,
  },
  {
    key: '3',
    segment: '初中教师缺口/富余',
    2025: -29,
    2026: -229,
    2027: 76,
    2028: -7,
    2029: -517,
    2030: -345,
    2031: -114,
    2032: 710,
    2033: 654,
    2034: 636,
    2035: 451,
  },
  {
    key: '4',
    segment: '高中教师缺口/富余',
    2025: 20,
    2026: -208,
    2027: -110,
    2028: -131,
    2029: -8,
    2030: -57,
    2031: -370,
    2032: -264,
    2033: -124,
    2034: 399,
    2035: 389,
  },
]);

/* ---------- 列定义 ---------- */
const teacherColumns = [
  { title: '学段', dataIndex: 'segment', width: 100, fixed: 'left' },
  ...Array.from({ length: 21 }, (_, i) => ({
    title: String(2015 + i),
    dataIndex: String(2015 + i),
    width: 80,
  })),
];

const teacherSurplusColumns = [
  { title: '学段', dataIndex: 'segment', width: 150, fixed: 'left' },
  ...Array.from({ length: 11 }, (_, i) => ({
    title: String(2025 + i),
    dataIndex: String(2025 + i),
    width: 80,
  })),
];

/* ---------- 工具函数 ---------- */
const formatToInt = (v) => (v === '' || v == null ? '-' : Math.round(Number(v)).toLocaleString());
const getValueClass = (v) => {
  if (v === '' || v == null) return '';
  const n = Number(v);
  return n > 0 ? 'positive-value' : n < 0 ? 'negative-value' : '';
};

/* ---------- 图表 ---------- */
const prepareChartData = () => {
  const years = Array.from({ length: 21 }, (_, i) => String(2015 + i));
  const kd = [],
    pd = [],
    jd = [],
    sd = [],
    td = [];
  years.forEach((y) => {
    let k = 0,
      p = 0,
      j = 0,
      s = 0;
    teacherData.value.forEach((r) => {
      const v = Number(r[y]);
      if (r.segment === '幼儿园') k = v;
      if (r.segment === '小学') p = v;
      if (r.segment === '初中') j = v;
      if (r.segment === '普通高中') s = v;
    });
    kd.push(k);
    pd.push(p);
    jd.push(j);
    sd.push(s);
    td.push(k + p + j + s);
  });
  return {
    years,
    kindergartenData: kd,
    primaryData: pd,
    juniorData: jd,
    seniorData: sd,
    totalData: td,
  };
};

const teacherChartOption = computed(() => {
  const { years, kindergartenData, primaryData, juniorData, seniorData } = prepareChartData();
  return {
    title: {
      text: '玉溪市2015-2035年各学段专任教师人数（堆叠柱形图）',
      left: 'center',
      textStyle: { fontSize: 26, fontWeight: 'bold' },
      top: 0,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        let res = `${params[0].axisValue}<br/>`;
        let tot = 0;
        params.forEach((p) => {
          tot += p.value;
          res += `<span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background:${
            p.color
          }"></span>${p.seriesName}: ${p.value.toLocaleString()}<br/>`;
        });
        res += `<hr style="margin:5px 0;border:none;border-top:1px solid #ccc"><strong>全市合计: ${tot.toLocaleString()}</strong>`;
        return res;
      },
    },
    legend: { data: ['幼儿园', '小学', '初中', '普通高中'], top: 30 },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: years, axisLabel: { interval: 0, rotate: 45 } },
    yAxis: { type: 'value', name: '教师人数', axisLabel: { formatter: (v) => v.toLocaleString() } },
    series: [
      {
        name: '幼儿园',
        type: 'bar',
        stack: 'total',
        data: kindergartenData,
        itemStyle: { color: '#91cc75' },
        label: { show: true, position: 'top', fontSize: 10, color: '#333' },
      },
      {
        name: '小学',
        type: 'bar',
        stack: 'total',
        data: primaryData,
        itemStyle: { color: '#fac858' },
        label: { show: true, position: 'top', fontSize: 10, color: '#333' },
      },
      {
        name: '初中',
        type: 'bar',
        stack: 'total',
        data: juniorData,
        itemStyle: { color: '#ee6666' },
        label: { show: true, position: 'top', fontSize: 10, color: '#333' },
      },
      {
        name: '普通高中',
        type: 'bar',
        stack: 'total',
        data: seniorData,
        itemStyle: { color: '#73c0de' },
        label: { show: true, position: 'top', fontSize: 10, color: '#333' },
      },
    ],
  };
});

/* ---------- 默认滚到最右 ---------- */
onMounted(() => {
  const scrollMaxRight = (ref) => {
    const body = ref.value?.$el?.querySelector('.arco-table-body');
    if (body) body.scrollLeft = body.scrollWidth;
  };
  scrollMaxRight(teacherTableRef);
  scrollMaxRight(surplusTableRef);
});
</script>

<style scoped>
.container {
  height: 100vh;
  background: #f5f5f5;
}
.header {
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  padding: 0 24px;
  display: flex;
  align-items: center;
}
.header h2 {
  margin: 0;
  color: #1f1f1f;
}
.content {
  padding: 16px;
  overflow-y: auto;
}
.tables-section {
  margin-bottom: 16px;
}
.chart-section {
  margin-top: 16px;
}
.table-card,
.chart-card {
  height: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
}
.chart-container {
  height: 100%;
}
.table-note {
  margin-top: 16px;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 4px;
  border-left: 4px solid #1890ff;
  font-size: 12px;
  color: #666;
}
.positive-value {
  color: #52c41a;
  font-weight: 500;
}
.negative-value {
  color: #f5222d;
  font-weight: 500;
}
:deep(.arco-card-body) {
  padding: 20px;
}
:deep(.arco-table) {
  font-size: 12px;
}
:deep(.arco-table-th) {
  font-weight: bold;
  background: #fafafa;
}
@media (max-width: 1200px) {
  .tables-section .arco-col-12 {
    width: 100%;
  }
}
</style>
